import React from "react";
import styles from "./CompetitionOffers.module.css";
import type { OfferPageTemplateResponse } from "@/types/offers";
import type { Site } from "@/types/sites";
import { getImageUrl } from "@/utils/utils";
import { handleOfferClick } from "@/components/OfferPage/OfferList";
import { getGAAttributes } from "@/utils/gaUtils";
import { CmsImage } from "../../common/CmsImage";
import Countdown from "./Countdown";
import { ClickoutLocation } from "@/types/offers";

interface CompetitionOffersProps {
  offerPage: OfferPageTemplateResponse;
  site: Site;
  initialViewportWidth: number;
}

const TicketSVG = ({ color = "#111" }) => (
  <svg
    width="21"
    height="15"
    viewBox="0 0 14 10"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
    style={{ display: "inline", verticalAlign: "middle" }}
  >
    <path d="M7.00244 8.12622C7.17952 8.12622 7.32796 8.06632 7.44775 7.94653C7.56755 7.82674 7.62744 7.6783 7.62744 7.50122C7.62744 7.32414 7.56755 7.1757 7.44775 7.05591C7.32796 6.93612 7.17952 6.87622 7.00244 6.87622C6.82536 6.87622 6.67692 6.93612 6.55713 7.05591C6.43734 7.1757 6.37744 7.32414 6.37744 7.50122C6.37744 7.6783 6.43734 7.82674 6.55713 7.94653C6.67692 8.06632 6.82536 8.12622 7.00244 8.12622ZM7.00244 5.62622C7.17952 5.62622 7.32796 5.56632 7.44775 5.44653C7.56755 5.32674 7.62744 5.1783 7.62744 5.00122C7.62744 4.82414 7.56755 4.6757 7.44775 4.55591C7.32796 4.43612 7.17952 4.37622 7.00244 4.37622C6.82536 4.37622 6.67692 4.43612 6.55713 4.55591C6.43734 4.6757 6.37744 4.82414 6.37744 5.00122C6.37744 5.1783 6.43734 5.32674 6.55713 5.44653C6.67692 5.56632 6.82536 5.62622 7.00244 5.62622ZM7.00244 3.12622C7.17952 3.12622 7.32796 3.06632 7.44775 2.94653C7.56755 2.82674 7.62744 2.6783 7.62744 2.50122C7.62744 2.32414 7.56755 2.1757 7.44775 2.05591C7.32796 1.93612 7.17952 1.87622 7.00244 1.87622C6.82536 1.87622 6.67692 1.93612 6.55713 2.05591C6.43734 2.1757 6.37744 2.32414 6.37744 2.50122C6.37744 2.6783 6.43734 2.82674 6.55713 2.94653C6.67692 3.06632 6.82536 3.12622 7.00244 3.12622ZM12.0024 10.0012H2.00244C1.65869 10.0012 1.36442 9.87882 1.11963 9.63403C0.874837 9.38924 0.752441 9.09497 0.752441 8.75122V6.25122C1.09619 6.25122 1.39046 6.12882 1.63525 5.88403C1.88005 5.63924 2.00244 5.34497 2.00244 5.00122C2.00244 4.65747 1.88005 4.3632 1.63525 4.11841C1.39046 3.87362 1.09619 3.75122 0.752441 3.75122V1.25122C0.752441 0.907471 0.874837 0.6132 1.11963 0.368408C1.36442 0.123617 1.65869 0.0012207 2.00244 0.0012207H12.0024C12.3462 0.0012207 12.6405 0.123617 12.8853 0.368408C13.13 0.6132 13.2524 0.907471 13.2524 1.25122V3.75122C12.9087 3.75122 12.6144 3.87362 12.3696 4.11841C12.1248 4.3632 12.0024 4.65747 12.0024 5.00122C12.0024 5.34497 12.1248 5.63924 12.3696 5.88403C12.6144 6.12882 12.9087 6.25122 13.2524 6.25122V8.75122C13.2524 9.09497 13.13 9.38924 12.8853 9.63403C12.6405 9.87882 12.3462 10.0012 12.0024 10.0012Z" fill={color} />
  </svg>
);

const CompetitionOffers: React.FC<CompetitionOffersProps> = ({ offerPage, site }) => {
  const offers = offerPage.sanitisedOffer || [];

  return (
    <section className={styles.competitionOffersGrid} aria-label="Competition Offers">
      {offers.map((offer, index) => {
        const merchant = offer.merchant?.data?.attributes;
        const merchantLogo = merchant?.logo;
        const merchantLogoAlt = merchant?.name ? `${merchant.name} logo` : "Merchant logo";
        const backgroundImage = getImageUrl(offer.offerImage?.data?.attributes?.url);
        const deadline = offer.deadline ? new Date(offer.deadline) : null;
        const gaAttributes = getGAAttributes(offer, index);
        // Dynamic merchant colors
        const merchantTextColor = merchant?.textColour || "#111";
        const merchantBgColor = merchant?.colour || "#ffe600";
        const formAttributes = {
          subId: offer?.subId ?? "",
          epcId: offer?.epcId ?? "",
          merchant: merchant?.name ?? "",
        };
        // Dynamic merchant colors
        const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
          console.log("[handleSubmit] offer:", offer);
          handleOfferClick(
            e,
            offer,
            index,
            offerPage,
            site,
            offer.epcId ?? '',
            gaAttributes,
            ClickoutLocation.CompetitionGrid
          );
        };
        return (
          <form
            key={offer.id}
            onSubmit={handleSubmit}
            id={`offer-${offer.id}`}
            className={styles.competitionCard}
            data-attr={JSON.stringify(formAttributes)}
            data-ga-attributes={JSON.stringify(gaAttributes)}
            tabIndex={0}
            aria-label={`Enter ${offer.title}`}
          >
            {/* Top Section - Image and Content */}
            <div className={styles.cardTop}>
              {/* Left - Image */}
              <div
                className={styles.cardImageSection}
                aria-label="Offer image"
              >

                {offer.offerImage && (
                  <div className={styles.backgroundImageWrapper}>
                    <CmsImage
                      cmsMedia={offer.offerImage}
                      maxWidth={260}
                      responsive={false}
                      desiredHeight={260}
                      defaultAlt={offer.title}
                      loading={index < 5 ? "eager" : "lazy"}
                      style={{ objectFit: 'cover', height: '100%' }}
                    />
                  </div>
                )}


                {merchantLogo && (
                  <div className={styles.merchantLogo}>
                    <CmsImage
                      cmsMedia={merchantLogo}
                      maxWidth={100}
                      responsive={false}
                      desiredHeight={40}
                      defaultAlt={merchantLogoAlt}
                      loading="lazy"
                    />
                  </div>
                )}
              </div>
              {/* Right - Content */}
              <div className={styles.cardContentSection}>
                <div className={styles.cardContent}>
                  <h3 className={styles.offerTitle}>{offer.title}</h3>
                  {offer.tagline && <p className={styles.offerSubtitle}>{offer.tagline}</p>}
                  <button
                    type="submit"
                    className={styles.enterButton}
                    aria-label={`Enter ${offer.title}`}
                  >
                    Enter Now
                  </button>
                  {offer.termsAndConditionsHTML ? (
                    <span className={styles.termsText} dangerouslySetInnerHTML={{ __html: offer.termsAndConditionsHTML }} />
                  ) : null}
                </div>
              </div>
            </div>

            {/* Bottom Section - Ticket and Countdown */}
            <div className={styles.cardBottom}>
              {/* Left - Ticket Info */}
              <div
                className={styles.ticketSection}
                style={{ background: merchantBgColor, color: merchantTextColor }}
              >
                <span className={styles.ticketIcon} style={{ color: merchantTextColor }}>
                  <TicketSVG color={merchantTextColor} />
                </span>
                <span className={styles.ticketText}>Ticket: {offer.ticket_price || "-"}</span>
              </div>

              {/* Right - Countdown */}
              <div className={styles.countdownSection}>
                {deadline ? (
                  <Countdown deadline={deadline} />
                ) : (
                  <span>No deadline</span>
                )}
              </div>
            </div>
          </form>
        );
      })}
    </section>
  );
};

export default CompetitionOffers; 